// Shared tools for keeping pages short: collapsible sections, paging, and
// frames that scroll on their own instead of growing the page.
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const readOpen = (key, fallback) => {
  try { const saved = key && localStorage.getItem(key); return saved == null ? fallback : saved === '1'; }
  catch { return fallback; }
};

// A section that can be folded away. With an `id`, the open state is remembered
// per browser. `summary` is a one-line preview shown only while closed.
export function Collapsible({ id, title, icon: Icon, meta, summary, defaultOpen = false, className = '', children, ...props }) {
  const key = id ? `spcc:open:${id}` : null;
  const [open, setOpen] = useState(() => readOpen(key, defaultOpen));
  const toggle = e => {
    const next = e.currentTarget.open;
    if (next === open) return;
    setOpen(next);
    try { if (key) localStorage.setItem(key, next ? '1' : '0'); } catch { /* storage unavailable */ }
  };
  return <details className={`v2-collapsible ${className}`} open={open} onToggle={toggle} {...props}>
    <summary>
      <ChevronRight className="v2-collapsible-chevron" aria-hidden="true"/>
      {Icon && <Icon aria-hidden="true"/>}
      <span className="v2-collapsible-title">{title}</span>
      {meta !== undefined && meta !== null && <span className="v2-tag">{meta}</span>}
      {summary && <span className="v2-collapsible-summary">{summary}</span>}
    </summary>
    <div className="v2-collapsible-body">{children}</div>
  </details>;
}

// Splits `items` into pages. Changing `resetKey` (e.g. the active filters) returns to page 1.
export function usePaged(items, size, resetKey) {
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [resetKey]);
  const pages = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(page, pages - 1);
  return { page: current, pages, size, total: items.length, setPage, items: items.slice(current * size, current * size + size) };
}

export function Pager({ paged, label = 'Pages', className = '' }) {
  if (paged.pages <= 1) return null;
  const start = paged.page * paged.size + 1;
  const end = Math.min(paged.total, start + paged.size - 1);
  return <nav className={`v2-pager ${className}`} aria-label={label}>
    <span>{start}–{end} of {paged.total}</span>
    <button type="button" aria-label="Previous page" disabled={paged.page === 0} onClick={() => paged.setPage(paged.page - 1)}><ChevronLeft aria-hidden="true"/></button>
    <button type="button" aria-label="Next page" disabled={paged.page >= paged.pages - 1} onClick={() => paged.setPage(paged.page + 1)}><ChevronRight aria-hidden="true"/></button>
  </nav>;
}

// On wide screens, returns a max-height that makes the element end exactly at the
// bottom of the window, so it scrolls on its own and the page itself stays short.
// Narrower screens stack content, so this returns null and CSS caps apply instead.
export function useFitHeight(ref, { min = 320, breakpoint = 1024 } = {}) {
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => {
      if (window.innerWidth <= breakpoint) return setHeight(null);
      const top = el.getBoundingClientRect().top + window.scrollY;
      const main = el.closest('.main-content');
      const bottomGap = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 24;
      setHeight(Math.max(min, Math.floor(window.innerHeight - top - bottomGap)));
    };
    fit();
    // Content above the frame (header, stats, a section opening, data arriving
    // after mount) changes the page's height, so watch the whole content area.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(el.closest('.main-content') || document.body);
    window.addEventListener('resize', fit);
    return () => { observer?.disconnect(); window.removeEventListener('resize', fit); };
  }, [ref, min, breakpoint]);
  return height;
}
